// src/pages/PublisherProfilePage.jsx
// Phase P1.5: Publisher Business Profile Form with Logo Upload
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PublisherNav from '../components/PublisherNav';
import { Building2, ArrowLeft, Save, AlertCircle, Upload, X, ImagePlus, Check } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNotification } from '@/contexts/NotificationContext';
import { BACKEND_URL as backendUrl } from '@/config/api';

const PublisherProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const { notifyError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showEinInput, setShowEinInput] = useState(false);
  
  // Logo state
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    legal_business_name: '',
    dba_name: '',
    ein: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    primary_contact_name: '',
    primary_contact_phone: '',
    primary_contact_email: ''
  });
  
  const [originalEin, setOriginalEin] = useState('');
  const [errors, setErrors] = useState({});
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await fetch(`${backendUrl}/api/publisher/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.exists !== false) {
          setFormData({
            legal_business_name: data.legal_business_name || '',
            dba_name: data.dba_name || '',
            ein: data.ein || '',
            address_line1: data.address_line1 || '',
            address_line2: data.address_line2 || '',
            city: data.city || '',
            state: data.state || '',
            postal_code: data.postal_code || '',
            country: data.country || 'US',
            primary_contact_name: data.primary_contact_name || '',
            primary_contact_phone: data.primary_contact_phone || '',
            primary_contact_email: data.primary_contact_email || ''
          });
          setOriginalEin(data.ein || '');
          setIsEditing(true);
          
          // Set logo URL if exists - add cache-busting timestamp
          if (data.imprint_logo_url) {
            setLogoUrl(`${backendUrl}${data.imprint_logo_url}?t=${data.updated_at || Date.now()}`);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      notifyError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Logo upload handlers
  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      notifyError('Invalid file type. Please upload PNG, JPG, or WebP.');
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      notifyError('File too large. Maximum size is 2MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target?.result);
    reader.readAsDataURL(file);

    // Upload if profile already exists
    if (isEditing) {
      await uploadLogo(file);
    }
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('logo', file);

      const response = await fetch(`${backendUrl}/api/publisher/me/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setLogoUrl(`${backendUrl}${data.imprint_logo_url}`);
        setLogoPreview(null);
        console.log('[SUCCESS] ' + 'Logo uploaded successfully');
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to upload logo');
        setLogoPreview(null);
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      notifyError('Failed to upload logo');
      setLogoPreview(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const required = [
      'legal_business_name', 'address_line1', 'city', 'state', 'postal_code',
      'primary_contact_name', 'primary_contact_phone', 'primary_contact_email'
    ];
    
    required.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });
    
    if (!isEditing && !formData.ein?.trim()) {
      newErrors.ein = 'EIN is required';
    }
    if (showEinInput && !formData.ein?.trim()) {
      newErrors.ein = 'EIN is required';
    }
    
    if (formData.ein && !formData.ein.includes('*')) {
      const einClean = formData.ein.replace(/-/g, '').replace(/\s/g, '');
      if (!/^\d{9}$/.test(einClean)) {
        newErrors.ein = 'EIN must be 9 digits (format: XX-XXXXXXX)';
      }
    }
    
    if (formData.primary_contact_email && !formData.primary_contact_email.includes('@')) {
      newErrors.primary_contact_email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      notifyError('Please fix the errors before submitting');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      const dataToSend = { ...formData };
      if (isEditing && formData.ein.includes('*') && !showEinInput) {
        delete dataToSend.ein;
      }
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(`${backendUrl}/api/publisher/me`, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[SUCCESS] ' + isEditing ? 'Profile updated successfully' : 'Profile created successfully');
        setFormData(prev => ({
          ...prev,
          ein: data.ein || prev.ein
        }));
        setOriginalEin(data.ein || '');
        setShowEinInput(false);
        setIsEditing(true);
        
        // Set logo URL if returned
        if (data.imprint_logo_url) {
          setLogoUrl(`${backendUrl}${data.imprint_logo_url}`);
        }
      } else {
        const error = await response.json();
        notifyError(error.detail || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      notifyError('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-blue-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
            <p className="mt-4 text-slate-400">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentLogo = logoPreview || logoUrl;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      <main className="mx-auto max-w-3xl px-4 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/publisher')}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-blue-600/20 p-2">
              <Building2 className="w-6 h-6 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold">
              {isEditing ? 'Edit Publisher Profile' : 'Create Publisher Profile'}
            </h1>
          </div>
          <p className="text-slate-400">
            {isEditing 
              ? 'Update your business information and imprint logo.'
              : 'Complete your business profile to unlock publisher features.'
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Imprint Logo Section */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Imprint Logo</h2>
            <div className="space-y-4">
              {/* Logo Preview/Upload - Large Rectangle */}
              <div className="relative">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoSelect}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                />
                {currentLogo ? (
                  <div className="relative group">
                    <img
                      src={currentLogo}
                      alt="Imprint Logo"
                      className="w-full max-w-[1000px] h-auto max-h-[300px] rounded-xl object-contain border border-slate-600 bg-white/5"
                    />
                    <button
                      type="button"
                      onClick={handleLogoClick}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity"
                    >
                      <div className="flex flex-col items-center text-white">
                        <Upload className="w-8 h-8 mb-2" />
                        <span className="text-sm">Click to replace logo</span>
                      </div>
                    </button>
                    {uploadingLogo && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-xl">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleLogoClick}
                    disabled={uploadingLogo}
                    className="w-full max-w-[600px] h-40 rounded-xl border-2 border-dashed border-slate-600 bg-slate-800 flex flex-col items-center justify-center hover:border-blue-500 hover:bg-slate-700 transition-colors disabled:opacity-50"
                  >
                    {uploadingLogo ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
                    ) : (
                      <>
                        <ImagePlus className="w-12 h-12 text-slate-400 mb-3" />
                        <span className="text-sm text-slate-400">Click to upload logo</span>
                        <span className="text-xs text-slate-500 mt-1">PNG, JPG, or WebP up to 2MB</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              
              {/* Logo Guidelines */}
              <div className="text-sm text-slate-400 bg-slate-800/50 rounded-lg p-4">
                <p className="font-medium text-slate-300 mb-2">Logo Guidelines</p>
                <ul className="space-y-1 text-xs">
                  <li>• Accepted formats: PNG, JPG, WebP</li>
                  <li>• Maximum file size: 2MB</li>
                  <li>• Recommended: Rectangle up to 1000px wide</li>
                  <li>• Your logo will appear on your dashboard and public pages</li>
                </ul>
                {!isEditing && (
                  <p className="mt-3 text-xs text-amber-400">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    Save your profile first to upload a logo
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Business Information */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Business Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="legal_business_name">Legal Business Name *</Label>
                <Input
                  id="legal_business_name"
                  name="legal_business_name"
                  value={formData.legal_business_name}
                  onChange={handleChange}
                  placeholder="Acme Publishing LLC"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.legal_business_name ? 'border-red-500' : ''}`}
                />
                {errors.legal_business_name && (
                  <p className="text-red-400 text-xs mt-1">{errors.legal_business_name}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="dba_name">DBA Name (Optional)</Label>
                <Input
                  id="dba_name"
                  name="dba_name"
                  value={formData.dba_name}
                  onChange={handleChange}
                  placeholder="Doing Business As..."
                  className="mt-1 bg-slate-800 border-slate-600"
                />
              </div>
              
              <div>
                <Label htmlFor="ein">EIN (Employer Identification Number) *</Label>
                {isEditing && !showEinInput ? (
                  <div className="mt-1 flex items-center gap-2">
                    <Input
                      value={originalEin}
                      disabled
                      className="bg-slate-800 border-slate-600 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowEinInput(true);
                        setFormData(prev => ({ ...prev, ein: '' }));
                      }}
                      className="px-3 py-2 text-sm bg-slate-700 hover:bg-slate-600 rounded-md transition-colors"
                    >
                      Update
                    </button>
                  </div>
                ) : (
                  <>
                    <Input
                      id="ein"
                      name="ein"
                      value={formData.ein}
                      onChange={handleChange}
                      placeholder="XX-XXXXXXX"
                      className={`mt-1 bg-slate-800 border-slate-600 font-mono ${errors.ein ? 'border-red-500' : ''}`}
                    />
                    {showEinInput && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowEinInput(false);
                          setFormData(prev => ({ ...prev, ein: originalEin }));
                        }}
                        className="text-xs text-slate-400 hover:text-slate-200 mt-1"
                      >
                        Cancel
                      </button>
                    )}
                  </>
                )}
                {errors.ein && (
                  <p className="text-red-400 text-xs mt-1">{errors.ein}</p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Your EIN is stored securely and masked for privacy.
                </p>
              </div>
            </div>
          </section>

          {/* Address */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Business Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="address_line1">Address Line 1 *</Label>
                <Input
                  id="address_line1"
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.address_line1 ? 'border-red-500' : ''}`}
                />
                {errors.address_line1 && (
                  <p className="text-red-400 text-xs mt-1">{errors.address_line1}</p>
                )}
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                <Input
                  id="address_line2"
                  name="address_line2"
                  value={formData.address_line2}
                  onChange={handleChange}
                  placeholder="Suite 100"
                  className="mt-1 bg-slate-800 border-slate-600"
                />
              </div>
              
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="New York"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.city ? 'border-red-500' : ''}`}
                />
                {errors.city && (
                  <p className="text-red-400 text-xs mt-1">{errors.city}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="state">State *</Label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className={`mt-1 w-full rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-sm ${errors.state ? 'border-red-500' : ''}`}
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-red-400 text-xs mt-1">{errors.state}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="postal_code">Postal Code *</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="10001"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.postal_code ? 'border-red-500' : ''}`}
                />
                {errors.postal_code && (
                  <p className="text-red-400 text-xs mt-1">{errors.postal_code}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  disabled
                  className="mt-1 bg-slate-800 border-slate-600"
                />
              </div>
            </div>
          </section>

          {/* Primary Contact */}
          <section className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6">
            <h2 className="text-lg font-semibold mb-4">Primary Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="primary_contact_name">Contact Name *</Label>
                <Input
                  id="primary_contact_name"
                  name="primary_contact_name"
                  value={formData.primary_contact_name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.primary_contact_name ? 'border-red-500' : ''}`}
                />
                {errors.primary_contact_name && (
                  <p className="text-red-400 text-xs mt-1">{errors.primary_contact_name}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="primary_contact_phone">Phone *</Label>
                <Input
                  id="primary_contact_phone"
                  name="primary_contact_phone"
                  value={formData.primary_contact_phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.primary_contact_phone ? 'border-red-500' : ''}`}
                />
                {errors.primary_contact_phone && (
                  <p className="text-red-400 text-xs mt-1">{errors.primary_contact_phone}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="primary_contact_email">Email *</Label>
                <Input
                  id="primary_contact_email"
                  name="primary_contact_email"
                  type="email"
                  value={formData.primary_contact_email}
                  onChange={handleChange}
                  placeholder="contact@publisher.com"
                  className={`mt-1 bg-slate-800 border-slate-600 ${errors.primary_contact_email ? 'border-red-500' : ''}`}
                />
                {errors.primary_contact_email && (
                  <p className="text-red-400 text-xs mt-1">{errors.primary_contact_email}</p>
                )}
              </div>
            </div>
          </section>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/publisher')}
              className="px-6 py-2 rounded-full border border-slate-600 text-slate-300 hover:bg-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Update Profile' : 'Create Profile'}
                </>
              )}
            </button>
          </div>
        </form>
      </main>
      <UniversalFooter />
    </div>
  );
};

export default PublisherProfilePage;
